import { type AccountErrorCode, accountErrorTexts } from "@nexxt/account-ui";
import type { ErrorTexts } from "@nexxt/app-kit";
import type { CatalogErrorCode } from "@nexxt/contracts/catalog";
import type { FilesErrorCode } from "@nexxt/contracts/files";
import type { LogisticsErrorCode } from "@nexxt/contracts/logistics";
import type { NotificationsErrorCode } from "@nexxt/contracts/notifications";
import type { PartyErrorCode } from "@nexxt/contracts/party";
import type { ProcurementErrorCode } from "@nexxt/contracts/procurement";
import { m } from "../paraglide/messages.js";

/** The codes of the catalog the brand's screens can meet (ExternalCatalogService, ADR-166, ADR-167). */
type AtelierCatalogErrorCode = Extract<
  CatalogErrorCode,
  | "linesheet_name_taken"
  | "linesheet_header_invalid"
  | "linesheet_not_editable"
  | "linesheet_not_ready_for_submission"
  | "linesheet_lines_invalid"
  | "line_deletion_needs_request"
  | "attributes_not_editable"
  | "attributes_invalid"
  | "attributes_incomplete"
  | "sync_in_progress"
  | "import_in_progress"
  | "import_file_not_usable"
  | "product_images_invalid"
>;

/**
 * The codes of procurement the screens of the supplier and of the brand can meet (ExternalProcurementService,
 * ADR-168, ADR-169, ADR-171, ADR-174).
 */
type AtelierProcurementErrorCode = Extract<
  ProcurementErrorCode,
  | "purchase_order_not_ready_for_confirmation"
  | "po_changes_invalid"
  | "inbound_invalid"
  | "inbound_not_editable"
  | "inbound_not_trackable"
  | "inbound_pickup_not_editable"
  | "inbound_not_ready_for_submission"
  | "inbound_documents_invalid"
  | "extra_units_inbound_fixed"
  | "missing_items_not_open"
  | "missing_items_invalid"
  | "credit_note_not_open"
  | "credit_note_invalid"
>;

/**
 * The codes of party the screens of an organization's administrator can meet (ExternalPartyService, ADR-172); an email
 * taken or locked the user form says itself (@nexxt/party-ui).
 */
type AtelierPartyErrorCode = Extract<
  PartyErrorCode,
  | "user_invalid"
  | "user_roles_invalid"
  | "own_access"
  | "user_administered_by_tomorrow"
  | "contact_invalid"
  | "contact_not_erasable"
  | "contact_erasure_in_progress"
>;

/**
 * The codes of logistics the supplier's vendor returns can meet (ExternalLogisticsService, ADR-227): its confirmation
 * finds the warehouse or Business Central without a code they need, which only Tomorrow sets right.
 */
type AtelierLogisticsErrorCode = Extract<LogisticsErrorCode, "stock_movement_not_ready">;

/** Following one object more than the limit (ADR-281); push on a device (ADR-283). */
type AtelierNotificationsErrorCode = Extract<
  NotificationsErrorCode,
  "follow_limit_reached" | "push_unavailable" | "push_subscription_invalid" | "push_endpoint_taken"
>;

// The words of the codes gateway-atelier can answer with (ADR-046), registered at start (main.tsx); the common ones
// are app-kit's, those of the sign-in and of the account @nexxt/account-ui's (ADR-207). Atelier explains what
// Backstage names (ADR-154, point 2): what happened and what to do. A code without a text does not compile.
export const errorTexts: ErrorTexts<
  | AccountErrorCode
  | AtelierCatalogErrorCode
  | AtelierProcurementErrorCode
  | AtelierPartyErrorCode
  | AtelierLogisticsErrorCode
  | AtelierNotificationsErrorCode
  | FilesErrorCode
> = {
  ...accountErrorTexts,
  stock_movement_not_ready: m.error_vendor_return_not_ready,
  follow_limit_reached: m.error_follow_limit_reached,
  push_unavailable: m.error_push_unavailable,
  push_subscription_invalid: m.error_push_subscription_invalid,
  push_endpoint_taken: m.error_push_endpoint_taken,
  linesheet_name_taken: m.error_linesheet_name_taken,
  linesheet_header_invalid: m.error_linesheet_header_invalid,
  linesheet_not_editable: m.error_linesheet_not_editable,
  linesheet_not_ready_for_submission: m.error_linesheet_not_ready_for_submission,
  linesheet_lines_invalid: m.error_linesheet_lines_invalid,
  line_deletion_needs_request: m.error_line_deletion_needs_request,
  attributes_not_editable: m.error_attributes_not_editable,
  attributes_invalid: m.error_attributes_invalid,
  attributes_incomplete: m.error_attributes_incomplete,
  sync_in_progress: m.error_sync_in_progress,
  import_in_progress: m.error_import_in_progress,
  import_file_not_usable: m.error_import_file_not_usable,
  product_images_invalid: m.error_product_images_invalid,
  purchase_order_not_ready_for_confirmation: m.error_purchase_order_not_ready_for_confirmation,
  po_changes_invalid: m.error_po_changes_invalid,
  inbound_invalid: m.error_inbound_invalid,
  inbound_not_editable: m.error_inbound_not_editable,
  inbound_not_trackable: m.error_inbound_not_trackable,
  inbound_pickup_not_editable: m.error_inbound_pickup_not_editable,
  inbound_not_ready_for_submission: m.error_inbound_not_ready_for_submission,
  inbound_documents_invalid: m.error_inbound_documents_invalid,
  extra_units_inbound_fixed: m.error_extra_units_inbound_fixed,
  missing_items_not_open: m.error_missing_items_not_open,
  missing_items_invalid: m.error_missing_items_invalid,
  credit_note_not_open: m.error_credit_note_not_open,
  credit_note_invalid: m.error_credit_note_invalid,
  user_invalid: m.error_user_invalid,
  user_roles_invalid: m.error_user_roles_invalid,
  own_access: m.error_own_access,
  user_administered_by_tomorrow: m.error_user_administered_by_tomorrow,
  contact_invalid: m.error_contact_invalid,
  contact_not_erasable: m.error_contact_not_erasable,
  contact_erasure_in_progress: m.error_contact_erasure_in_progress,
  file_type_not_allowed: m.error_file_type_not_allowed,
  file_too_large: m.error_file_too_large,
  file_content_mismatch: m.error_file_content_mismatch,
  upload_incomplete: m.error_upload_incomplete,
  storage_unavailable: m.error_storage_unavailable,
};
